import { useEffect, useState } from "react";
import { useLocation, unstable_usePrompt } from "react-router-dom";

import {
  useDebounce,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../../../hooks";
import {
  IBank,
  ICreateBankTransfer,
  ICreateBankTransferBeneficiary,
  IHookForm,
  IPageAndSearchRequest,
  IResolveBankAccountResponse,
  ISwAppSupportedCountry,
  ISwAppSupportedCurrency,
  IViewBankTransferBeneficiary,
  IViewTransferFee,
  IViewTransferRate,
  NoMeta,
  PagedMeta,
} from "../../../../../interfaces-and-types";
import {
  generateRandomString,
  getCurrentDateFormatted,
  globalHandlerUtils,
} from "../../../../../utils";

import { singleBankTransferSchema } from "./singleBankTransferValidationSchema";
import { View } from "./View";

export type TNewOrBeneficiaryRecipient = "new" | "beneficiary";

export interface ISingleTransferViewProps
  extends IHookForm<ICreateBankTransfer> {
  navigateToBatchList: () => void;
  isBatchEditMode?: boolean;

  beneficiaries: IViewBankTransferBeneficiary[];
  setSearchBeneficiaryText: (search: string | null) => void;
  selectBeneficiary: (beneficiary: IViewBankTransferBeneficiary) => void;
  clearSelectedBeneficiary: () => void;
  isLoadingBeneficiaries: boolean;

  newOrBeneficiaryView: TNewOrBeneficiaryRecipient;
  setNewOrBeneficiaryView: (name: TNewOrBeneficiaryRecipient) => void;

  isLoadingSupportedCountries: boolean;
  swAppSupportedCountries: ISwAppSupportedCountry[];

  isLoadingSupportedCurrencies: boolean;
  swAppSupportedCurrencies: ISwAppSupportedCurrency[];

  isLoadingBankList: boolean;
  bankList: IBank[];
  formValues: ICreateBankTransfer;
  isDirty: boolean;

  isBankAccountResolved: boolean;
  setBankAccountResolved: (isResolved: boolean) => void;
  setResolvedBankAccountDetails: (
    resolvedDetails: IResolveBankAccountResponse
  ) => void;

  // transfer fees & rates
  destinationCountryIsNotNigeria: boolean;
  sourceCurrency: string;
  isLoadingTransferFee: boolean;
  isSuccessTransferFee: boolean;
  swAppTransferFee?: number;
  isLoadingTransferRate: boolean;
  isSuccessTransferRate: boolean;
  swAppTransferRate?: IViewTransferRate | undefined;
}

interface ICountrySelect {
  country: string;
}

interface ICountryAndCurrencySelect extends ICountrySelect {
  currency: string;
}

interface ITrsfFeeQueryParams extends ICountryAndCurrencySelect {
  amount: number;
}

interface ITrsfConvRateQueryParams {
  amount: number;
  source_currency: string;
  destination_currency: string;
}

export const SingleBankTransferForm = (props: {
  sourceCurrency: string;
  appendSingleBankTransfer: (singleBankTransfer: ICreateBankTransfer) => void;
  bankTransfersList: ICreateBankTransfer[];

  // for inline batch editing
  isBatchEditMode?: boolean;
  initialBatchEntryValues?: ICreateBankTransfer;
}) => {
  const { navigate } = useReactRouterLink();
  const location = useLocation();

  const [searchBeneficiaryText, setSearchBeneficiaryText] = useState<
    string | null
  >("");

  const {
    isSuccess: isSuccessCreateBankTransferBeneficiary,
    mutate: createBankTransferBeneficiary,
    data: serviceResponseViewBeneficiary,
  } = useMerchantApiMutation<
    ICreateBankTransferBeneficiary,
    IViewBankTransferBeneficiary
  >("createBankTransferBeneficiary", [
    "getBankTransferBeneficiaries",
  ]).usePostMutation;

  const {
    isLoading: isLoadingBeneficiaries,
    isError: isErrorBeneficiaries,
    isSuccess: isSuccessBeneficiaries,
    data: beneficiaries,
    meta: beneficiariesMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IViewBankTransferBeneficiary[],
    PagedMeta
  >([
    "getBankTransferBeneficiaries",
    {
      page: 1,
      page_size: 20,
      search: searchBeneficiaryText,
    },
  ]);

  const [isBankAccountResolved, setBankAccountResolved] = useState(false);

  const {
    isLoading: isLoadingSupportedCountries,
    data: swAppSupportedCountries,
  } = useMerchantApiQuery<ISwAppSupportedCountry[]>("getSupportedCountries");

  const initialFormValues: ICreateBankTransfer =
    props.initialBatchEntryValues ?? {
      reference: `SWAPP-TRSF-${getCurrentDateFormatted()}-${generateRandomString(
        15
      )}`,
      beneficiary_id: null,
      country: "NG",
      bank_code: "",
      account_number: "",
      amount: 0,
      currency: "NGN",
      description: "",
      save_as_beneficiary: true,
      wallet_account_tags: [],

      // international properties
      bank_name: "",
      account_name: "",
      sort_code: "",
      swift_code: "",
      email: "",
      phone_number: "",
      street_number: "",
      street_name: "",
      city: "",
      postal_code: "",

      isBeneficiaryResolved: false,
      isLoadingResolveBeneficiary: false,
      isErrorResolveBeneficiary: false,

      accountNameHelper: null,
      bankNameHelper: null,
      fullNameHelper: null,
      // sourceCurrencyDebitAmountHelper: null,
    };

  const handleAppendSingleTransferSubmit = async (
    data: ICreateBankTransfer
  ) => {
    const duplicateRefExists =
      !props.isBatchEditMode &&
      props.bankTransfersList.find((trsf) => trsf.reference === data.reference);

    if (duplicateRefExists) {
      setError("reference", {
        message: "Duplicate transfer reference!",
      });
      return; // duplicate
    }

    if (isResolvableBankAccountCtry && !isBankAccountResolved) {
      setError("account_number", {
        message: "Account details not resolved!",
      });
      return; // duplicate
    }

    if (!isResolvableBankAccountCtry) {
      setResolvedBankAccountDetails({
        // account_name: data.account_name || "",
        // account_number: data.account_number
        account_name: data.account_name || "",
        bank_name: data.bank_name || "",
        account_number: data.account_number || "",
        bank_code: data.bank_code || "",
        country: data.country || "",
        currency: data.currency || "",
      });
    }

    if (formValues.save_as_beneficiary) {
      /*await*/ createBankTransferBeneficiary(formValues); // mutateAsync cannot be awaited
    }

    props.appendSingleBankTransfer({
      // beneficiary_id: serviceResponseViewBeneficiary?.data.id, // may retrieve previous value b/c of mutateAsync behaviour

      ...data,
      amount: Number(data.amount), // amount is string from text box and needs conversion in <CurrencyDisplay />
      isBeneficiaryResolved: isBankAccountResolved,
    });

    reset({
      ...initialFormValues,
      country: data.country, // to save time from having to re-select
      currency: data.currency,
    });

    navigateToBatchList();
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    onFormSubmit,
    errors,
    register,
    reset,
    control,
    setValue,
    setError,
    // clearErrors,
    watch,
    isDirty,
  } = useHookForm({
    initialFormValues,
    validationSchema: singleBankTransferSchema,
    onValid: handleAppendSingleTransferSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();
  const transferAmount = Number(
    formValues.amount.toString().replace(",", "").replace(" ", "")
  );

  const isResolvableBankAccountCtry = formValues.country === "NG";

  //Error: useBlocker must be used within a data router.  See https://reactrouter.com/routers/picking-a-router.
  // unstable_usePrompt({
  //   when: isDirty,
  //   message:
  //     "Are you sure you want to leave? Your changes have not been submitted!",
  // });

  const debouncePeriodElapsed = useDebounce();

  const { isLoading: isLoadingBankList, data: bankList } =
    useMerchantApiQueryWithParams<ICountryAndCurrencySelect, IBank[], NoMeta>(
      [
        "getBanks",
        {
          country: formValues.country,
          currency: formValues.currency,
        },
      ],
      {
        enabled:
          debouncePeriodElapsed &&
          !!formValues.country &&
          isResolvableBankAccountCtry &&
          !!formValues.currency,
      }
    );

  const {
    isLoading: isLoadingSupportedCurrencies,
    data: swAppSupportedCurrencies,
  } = useMerchantApiQueryWithParams<
    ICountrySelect,
    ISwAppSupportedCurrency[],
    NoMeta
  >(
    [
      "getSupportedCurrencies",
      {
        country: formValues.country,
      },
    ],
    {
      enabled: !!formValues.country,
    }
  );

  useEffect(() => {
    if (!isBankAccountResolved) {
      setValue("accountNameHelper", null);
      setValue("bankNameHelper", null);
    }
  }, [isBankAccountResolved]);

  const setResolvedBankAccountDetails = (
    resolvedDetails: IResolveBankAccountResponse
  ) => {
    setValue("accountNameHelper", resolvedDetails.account_name);
    setValue("bankNameHelper", resolvedDetails.bank_name);
  };

  const navigateToBatchList = () => {
    navigate(location.pathname.replace("/single", ""));
  };

  const [newOrBeneficiaryView, setNewOrBeneficiaryView] =
    useState<TNewOrBeneficiaryRecipient>("beneficiary");

  useEffect(() => {
    if (
      isSuccessBeneficiaries &&
      beneficiaries &&
      beneficiaries?.length === 0 &&
      !searchBeneficiaryText
    )
      setNewOrBeneficiaryView("new");
  }, [isSuccessBeneficiaries]);

  // always clear search params after navigating away from list
  useEffect(() => setSearchBeneficiaryText(null), [newOrBeneficiaryView]);

  const debounceGetCurrenciesForCountry = useDebounce(150, formValues.country);

  const debounceGetTransferRatesAndFees = useDebounce(
    1000,
    `${props.sourceCurrency}-${formValues.currency}-${transferAmount}`
  );

  const destinationCountryIsNotNigeria =
    !!formValues.country && formValues.country !== "NG";

  const {
    // isLoading: isLoadingSupportedCurrenciesForCountry,
    isSuccess: isSuccessSupportedCurrenciesForCountry,
    data: swAppSupportedCurrenciesForCountry,
  } = useMerchantApiQueryWithParams<
    ICountrySelect,
    ISwAppSupportedCurrency[],
    NoMeta
  >(
    [
      "getSupportedCurrenciesByCountry",
      {
        country: formValues.country,
      },
    ],
    {
      enabled:
        debounceGetCurrenciesForCountry && destinationCountryIsNotNigeria,
    }
  );

  // set the currency of the country by default
  useEffect(() => {
    if (
      isSuccessSupportedCurrenciesForCountry &&
      swAppSupportedCurrenciesForCountry?.length
    ) {
      const defaultCurrencyForCountry = swAppSupportedCurrenciesForCountry[0];
      if (defaultCurrencyForCountry)
        setValue("currency", defaultCurrencyForCountry.currency);
    }
  }, [isSuccessSupportedCurrenciesForCountry]);

  // set an arbitrary bank code if not NGN
  useEffect(() => {
    if (isResolvableBankAccountCtry) setValue("bank_code", "");
    else setValue("bank_code", "--");

    if (!destinationCountryIsNotNigeria) setValue("currency", "NGN");
  }, [formValues.country]);

  const {
    isLoading: isLoadingTransferFee,
    isSuccess: isSuccessTransferFee,
    data: swAppTransferFee,
  } = useMerchantApiQueryWithParams<ITrsfFeeQueryParams, IViewTransferFee>(
    [
      "getTransferFee",
      {
        country: formValues.country,
        currency: formValues.currency,
        amount: transferAmount,
      },
    ],
    {
      enabled: debounceGetTransferRatesAndFees && transferAmount > 0,
    },
    undefined,
    {
      hideApiErrorMessge: true,
      hideAxiosErrorMessages: true,
      hideResponseMessage: true,
    }
  );

  const {
    isLoading: isLoadingTransferRate,
    isSuccess: isSuccessTransferRate,
    data: swAppTransferRate,
  } = useMerchantApiQueryWithParams<
    ITrsfConvRateQueryParams,
    IViewTransferRate
  >(
    [
      "getTransferRate",
      {
        source_currency: props.sourceCurrency,
        destination_currency: formValues.currency,
        amount: transferAmount,
      },
    ],
    {
      enabled:
        !!props.sourceCurrency &&
        debounceGetTransferRatesAndFees &&
        transferAmount > 0 &&
        destinationCountryIsNotNigeria,
    },
    undefined,
    {
      hideApiErrorMessge: true,
    }
  );

  const selectBeneficiary = (beneficiary: IViewBankTransferBeneficiary) => {
    reset({
      ...formValues,

      // ...beneficiary, // inserts benef description into transfer description
      beneficiary_id: beneficiary.id,
      country: beneficiary.country,
      bank_code: beneficiary.bank_code,
      account_number: beneficiary.account_number,
      currency: beneficiary.currency,
      save_as_beneficiary: false,

      bankNameHelper: beneficiary.bank_name,
      accountNameHelper: beneficiary.account_name,
      fullNameHelper: beneficiary.full_name,
    });
    // setBankAccountResolved(true);
    setSearchBeneficiaryText("");
  };

  const clearSelectedBeneficiary = () => {
    reset({
      // ...initialFormValues,
      ...formValues,
      beneficiary_id: null,
      bank_code: undefined,
      account_number: undefined,
      bankNameHelper: null,
      accountNameHelper: null,
      fullNameHelper: null,
    });

    setBankAccountResolved(false);
  };

  return (
    <View
      {...props}
      navigateToBatchList={navigateToBatchList}
      isBatchEditMode={props.isBatchEditMode}
      beneficiaries={beneficiaries || []}
      isLoadingBeneficiaries={isLoadingBeneficiaries}
      setSearchBeneficiaryText={setSearchBeneficiaryText}
      selectBeneficiary={selectBeneficiary}
      clearSelectedBeneficiary={clearSelectedBeneficiary}
      newOrBeneficiaryView={newOrBeneficiaryView}
      setNewOrBeneficiaryView={setNewOrBeneficiaryView}
      isLoadingSupportedCountries={isLoadingSupportedCountries}
      swAppSupportedCountries={swAppSupportedCountries || []}
      isLoadingSupportedCurrencies={isLoadingSupportedCurrencies}
      swAppSupportedCurrencies={swAppSupportedCurrencies || []}
      isLoadingBankList={isLoadingBankList}
      bankList={bankList || []}
      isBankAccountResolved={isBankAccountResolved}
      setBankAccountResolved={setBankAccountResolved}
      setResolvedBankAccountDetails={setResolvedBankAccountDetails}
      // fees & rates
      destinationCountryIsNotNigeria={destinationCountryIsNotNigeria}
      isLoadingTransferFee={isLoadingTransferFee}
      isSuccessTransferFee={isSuccessTransferFee}
      swAppTransferFee={swAppTransferFee?.fee}
      isLoadingTransferRate={isLoadingTransferRate}
      isSuccessTransferRate={isSuccessTransferRate}
      swAppTransferRate={swAppTransferRate}
      // batch
      formValues={formValues}
      isDirty={isDirty}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      control={control}
    />
  );
};
